import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import MethodGet, { MethodPost } from "../../config/Service";
import CardProduct from "../../components/Products/CardProduct";
import { SectionTitle } from "../../components/SectionTitle";
import ProductsContext from "../../Context/Products/ProductsContext";
import {
  Grid,
  Box,
  Pagination,
  useTheme,
  useMediaQuery,
  Typography,
  Fade,
  CircularProgress,
} from "@mui/material";
import { StorefrontRounded, ArrowBackRounded } from "@mui/icons-material";
import SearchProducts from "./SearchProducts";
import FilterPanel from "./FilterPanel";
import SEO from "../../components/SEO/SEO";

const CategoriesIndex = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const productsScrollRef = useRef(null);
  const { GetAllProducts } = useContext(ProductsContext);

  const [page, setPage] = useState(1);
  const [categories, setCategories] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allProducts, setAllProducts] = useState([]);
  const [categoryProducts, setCategoryProducts] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalProducts, setTotalProducts] = useState(0);
  const [searchLoading, setSearchLoading] = useState(false);

  // Filter states
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const [debouncedPriceRange, setDebouncedPriceRange] = useState([0, 10000]);
  const [isPriceRangeManuallySet, setIsPriceRangeManuallySet] = useState(false);
  const [sortBy, setSortBy] = useState("default");
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);

  // Fixed price range limits (server-side handles the actual filtering)
  const minPrice = 0;
  const maxPrice = 10000;

  // Debounce price range so API isn't called on every slider tick
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedPriceRange(priceRange);
    }, 500);
    return () => clearTimeout(timer);
  }, [priceRange]);

  // Load categories on mount
  useEffect(() => {
    MethodGet("/cate")
      .then((res) => {
        setCategories(res.data.categories);
        // Collect all subcategories from all categories
        const allSubcategories = res.data.categories.reduce((acc, cat) => {
          if (cat.subcategories) {
            return [...acc, ...cat.subcategories];
          }
          return acc;
        }, []);
        setSubcategories(allSubcategories);
      })
      .catch((error) => {
        console.error("Error al cargar categorías:", error);
      });
  }, []);

  // Load brands on mount
  useEffect(() => {
    MethodGet("/distribucion/products/brands")
      .then((res) => {
        setBrands(res.data || []);
      })
      .catch((error) => {
        console.error("Error al cargar marcas:", error);
      });
  }, []);

  // Build query params for API
  const buildQueryParams = () => {
    const params = new URLSearchParams();
    params.set("page", page);
    params.set("per_page", itemsPerPage);
    if (sortBy !== "default") params.set("sort_by", sortBy);
    if (isPriceRangeManuallySet) {
      params.set("min_price", debouncedPriceRange[0]);
      params.set("max_price", debouncedPriceRange[1]);
    }
    if (selectedBrand) params.set("brand_id", selectedBrand);
    return params.toString();
  };

  // Load all products on mount (default view)
  useEffect(() => {
    setLoading(true);
    MethodGet(`/distribucion/products/all?${buildQueryParams()}`)
      .then((res) => {
        setAllProducts(res.data.data || res.data);
        setTotalProducts(res.data.total || (res.data.data ? res.data.data.length : 0));
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error al cargar productos:", error);
        setLoading(false);
      });
  }, []);

  // Update subcategories when category changes
  useEffect(() => {
    if (selectedCategory === null) {
      const allSubcategories = categories.reduce((acc, cat) => {
        if (cat.subcategories) {
          return [...acc, ...cat.subcategories];
        }
        return acc;
      }, []);
      setSubcategories(allSubcategories);
    } else {
      const selectedCat = categories.find((cat) => cat.id === selectedCategory);
      setSubcategories(selectedCat?.subcategories || []);
    }
  }, [selectedCategory, categories]);

  // Only scroll after user explicitly interacts with filters
  const hasUserInteracted = useRef(false);
  const fetchTimer = useRef(null);

  // Fetch products based on filters — debounced to avoid cascading state updates
  useEffect(() => {
    // Clear any pending fetch to avoid duplicate calls
    if (fetchTimer.current) clearTimeout(fetchTimer.current);

    const shouldScroll = hasUserInteracted.current;

    const doFetch = () => {
      if (selectedCategory === null) {
        setLoading(true);
        MethodGet(`/distribucion/products/all?${buildQueryParams()}`)
          .then((res) => {
            setAllProducts(res.data.data || res.data);
            setTotalProducts(res.data.total || (res.data.data ? res.data.data.length : 0));
            setLoading(false);
          })
          .catch((error) => {
            console.error("Error:", error);
            setLoading(false);
          });
      } else {
        setLoading(true);
        const formData = new FormData();
        formData.append("category_id", selectedCategory);
        if (selectedSubcategory) formData.append("subcategory_id", selectedSubcategory);
        if (sortBy !== "default") formData.append("sort_by", sortBy);
        if (isPriceRangeManuallySet) {
          formData.append("min_price", debouncedPriceRange[0]);
          formData.append("max_price", debouncedPriceRange[1]);
        }

        // Match the endpoint used in context: /distribucion/ProductsCategory
        const url = `/distribucion/ProductsCategory?page=${page}&per_page=${itemsPerPage}`;

        MethodPost(url, formData)
          .then((res) => {
            // The API response structure might vary, adjust if needed based on context code
            // Context code was: res.data
            setCategoryProducts(res.data.data || res.data);
            setTotalProducts(res.data.total || (res.data.data ? res.data.data.length : 0));
            setLoading(false);
          })
          .catch((error) => {
            console.error("Error loading category products:", error);
            setLoading(false);
          });
      }
    };

    if (shouldScroll) {
      const el = document.getElementById("catalog-top");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      fetchTimer.current = setTimeout(doFetch, 200);
    } else {
      // Small debounce to batch cascading state updates (e.g. category + subcategory + page)
      fetchTimer.current = setTimeout(doFetch, 50);
    }

    return () => {
      if (fetchTimer.current) clearTimeout(fetchTimer.current);
    };
  }, [selectedCategory, selectedSubcategory, page, itemsPerPage, sortBy, debouncedPriceRange, selectedBrand]);

  // Get current products to display
  const currentProducts = selectedCategory === null ? allProducts : categoryProducts;

  const handlePageChange = (event, value) => {
    hasUserInteracted.current = true;
    setPage(value);
  };

  // Products are now filtered and sorted server-side
  const filteredAndSortedProducts = currentProducts || [];

  const handleClearFilters = () => {
    hasUserInteracted.current = true;
    setSelectedCategory(null);
    setSelectedSubcategory(null);
    setSelectedBrand(null);
    setIsPriceRangeManuallySet(false);
    setPriceRange([0, 10000]);
    setDebouncedPriceRange([0, 10000]);
    setSortBy("default");
    setPage(1);
  };

  const handlePriceRangeChange = (newRange) => {
    hasUserInteracted.current = true;
    setIsPriceRangeManuallySet(true);
    setPriceRange(newRange);
  };

  const history = useHistory();
  const location = useLocation();

  // Handle scroll from navigation state (e.g., from "All Categories")
  useEffect(() => {
    if (location.state?.scrollToSearch) {
      setTimeout(() => {
        const element = document.getElementById('catalog-top');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        // Optional: clear state to prevent scroll on refresh? 
        // managing history state replacement is tricky, so we just let it be.
      }, 400); // 0.4s delay
    }
  }, [location.state]);

  // ... (keep usage of history)

  const handleCategoryChange = (categoryId) => {
    hasUserInteracted.current = true;
    setSelectedCategory(categoryId || null);
    setSelectedSubcategory(null);
    setPage(1);
  };

  const handleSubcategoryChange = (subcategoryId) => {
    hasUserInteracted.current = true;
    setSelectedSubcategory(subcategoryId);
    setPage(1);
  };

  // Get current filter description
  const getFilterDescription = () => {
    if (selectedCategory === null) return "Todos los productos";
    const cat = categories.find((c) => c.id === selectedCategory);
    if (!cat) return "Productos";
    if (selectedSubcategory === null) {
      return cat.name;
    }
    const sub = subcategories.find((s) => s.idsubcategory === selectedSubcategory);
    return `${cat.name} • ${sub?.subcategoryName || ""}`;
  };

  return (
    <Box
      ref={productsScrollRef}
      component="section"
      sx={{
        padding: { xs: 2, md: 4 },
        backgroundColor: "#fff",
        minHeight: "100vh",
      }}
    >
      <SEO
        title="Catálogo"
        description="Explora todos nuestros productos de belleza, maquillaje y cuidado personal."
        url="/catalogo"
      />
      <Box sx={{ maxWidth: "1440px", margin: "0 auto" }}>
        {/* Header Section */}
        <Box sx={{ mb: 2, textAlign: "center" }}>
          <SectionTitle
            subTitle="¡Los más vendidos han llegado!"
            body="Descubre los productos más populares de la temporada y mantén viva la esencia del florecer junto a Merrycolor. ✨🌸"
          />

          {/* Brand Section - Redesigned */}
          {brands.length > 0 && (
            <Box
              sx={{
                mb: 6,
                py: 4,
                px: { xs: 2, md: 4 },
                background: 'linear-gradient(135deg, #fff5f8 0%, #fff 100%)',
                borderRadius: 4,
                position: 'relative',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0,0,0,0.02)',
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3,
                  maxWidth: '1440px',
                  mx: 'auto'
                }}
              >
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: '#801F56',
                      fontSize: { xs: '1.5rem', md: '2rem' },
                      letterSpacing: '-0.5px'
                    }}
                  >
                    Nuestras Marcas
                  </Typography>
                  <Typography sx={{ color: '#666', fontSize: '0.9rem', mt: 0.5 }}>
                    Calidad y exclusividad en cada producto
                  </Typography>
                </Box>

                <Typography
                  onClick={() => {
                    setSelectedBrand(null);
                    const element = document.getElementById('catalog-top');
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                  }}
                  sx={{
                    color: '#e94560',
                    fontWeight: 900,
                    fontSize: '0.95rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    transition: 'all 0.2s',
                    '&:hover': {
                      color: '#801F56',
                      transform: 'translateX(3px)'
                    }
                  }}
                >
                  Ver todas
                  <ArrowBackRounded sx={{ transform: 'rotate(180deg)', fontSize: 18 }} />
                </Typography>
              </Box>

              {/* Scroll Container */}
              <Box
                sx={{
                  display: 'flex',
                  gap: { xs: 3, md: 5 },
                  overflowX: 'auto',
                  pb: 4,
                  pt: 1,
                  px: 1,
                  maskImage: 'linear-gradient(to right, transparent, black 2%, black 98%, transparent)',
                  '&::-webkit-scrollbar': { display: 'none' },
                  scrollbarWidth: 'none',
                  maxWidth: '2144px',
                  mx: 'auto'
                }}
              >
                {brands.map((brand) => (
                  <Link
                    key={brand.id}
                    to={`/marca/${brand.name.toLowerCase().replace(/\s+/g, '-')}`}
                    style={{ textDecoration: 'none' }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1.5,
                        minWidth: 360,
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          '& .brand-ring': {
                            borderColor: '#e94560',
                            boxShadow: '0 12px 25px rgba(233,69,96,0.25)',
                            transform: 'scale(1.02)'
                          },
                          '& .brand-name': {
                            color: '#801F56',
                          }
                        },
                      }}
                    >
                      <Box
                        className="brand-ring"
                        sx={{
                          width: 230,
                          height: 230,
                          borderRadius: '50%',
                          padding: '0px',
                          background: '#fff',
                          border: '2px solid rgba(0,0,0,0.05)',
                          boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                          transition: 'all 0.3s ease',
                          display: 'flex',
                          alignItems: 'center',
                          marginTop: 3,
                          justifyContent: 'center',
                          position: 'relative',
                          overflow: 'hidden',
                        }}
                      >
                        {brand.image ? (
                          <Box
                            component="img"
                            src={brand.image}
                            alt={brand.name}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                              transition: 'transform 0.5s ease',
                            }}
                          />
                        ) : (
                          <StorefrontRounded sx={{ color: '#ccc', fontSize: 32 }} />
                        )}
                      </Box>
                      <Typography
                        className="brand-name"
                        sx={{
                          fontSize: '1.1rem',
                          fontWeight: 700,
                          color: '#444',
                          textAlign: 'center',
                          transition: 'color 0.3s ease',
                        }}
                      >
                        {brand.name}
                      </Typography>
                    </Box>
                  </Link>
                ))}
              </Box>
            </Box>
          )}

          <Box id="catalog-top" sx={{ maxWidth: "100%", margin: "0 auto", scrollMarginTop: "71px" }}>
            <SearchProducts results={results} setResults={setResults} onLoadingChange={setSearchLoading} />
          </Box>
        </Box>

        {results.length === 0 && !searchLoading && (
          <Grid container spacing={4}>
            {/* Filter Panel - Left Sidebar */}
            <Grid item xs={12} md={3}>
              <FilterPanel
                // Category props
                categories={categories}
                selectedCategory={selectedCategory}
                onCategoryChange={handleCategoryChange}
                subcategories={subcategories}
                selectedSubcategory={selectedSubcategory}
                onSubcategoryChange={handleSubcategoryChange}
                // Brand props
                brands={brands}
                selectedBrand={selectedBrand}
                onBrandChange={(val) => {
                  hasUserInteracted.current = true;
                  setSelectedBrand(val);
                  setPage(1);
                }}
                // Price props
                minPrice={minPrice}
                maxPrice={maxPrice}
                priceRange={priceRange}
                onPriceChange={handlePriceRangeChange}
                // Sort props
                sortBy={sortBy}
                onSortChange={(val) => { hasUserInteracted.current = true; setSortBy(val); }}
                // Clear
                onClearFilters={handleClearFilters}
                // Mobile
                isMobile={isMobile}
                isOpen={filterPanelOpen}
                onToggle={() => setFilterPanelOpen(!filterPanelOpen)}
              />
            </Grid>

            {/* Products Grid - Right Content */}
            <Grid item xs={12} md={9}>
              {/* Products Header */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                  pb: 2,
                  borderBottom: "1px solid #eee",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 800,
                        color: "#111",
                        fontSize: { xs: "1.25rem", md: "1.5rem" },
                      }}
                    >
                      {getFilterDescription()}
                    </Typography>
                    <Typography
                      sx={{
                        color: "#666",
                        fontSize: "0.9rem",
                        mt: 0.5,
                      }}
                    >
                      {totalProducts > 0
                        ? `Mostrando ${(page - 1) * itemsPerPage + 1} - ${Math.min(
                          page * itemsPerPage,
                          totalProducts
                        )} de ${totalProducts} productos`
                        : "No se encontraron productos"}
                    </Typography>
                  </Box>

                  {/* Items Per Page Selector */}
                  <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontSize: "0.9rem", color: "#666", display: { xs: "none", sm: "block" } }}>
                      Mostrar:
                    </Typography>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setPage(1);
                      }}
                      style={{
                        padding: "6px 12px",
                        borderRadius: "8px",
                        border: "1px solid #ddd",
                        backgroundColor: "#fff",
                        fontSize: "0.9rem",
                        color: "#333",
                        cursor: "pointer",
                        outline: "none",
                      }}
                    >
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </Box>
                </Box>
              </Box>

              {/* Loading State */}
              {loading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "400px",
                  }}
                >
                  <CircularProgress sx={{ color: "#801F56" }} />
                </Box>
              ) : (
                <>
                  {/* Products Grid */}
                  {filteredAndSortedProducts && filteredAndSortedProducts.length > 0 ? (
                    <Fade in={true} timeout={500}>
                      <Box>
                        <Grid container spacing={2}>
                          {filteredAndSortedProducts.map((product, index) => (
                            <Grid
                              item
                              xs={6}
                              sm={6}
                              md={4}
                              lg={4}
                              xl={3}
                              key={product.id || index}
                              sx={{
                                animation: `fadeInUp 0.4s ease ${index * 0.03}s both`,
                                "@keyframes fadeInUp": {
                                  from: {
                                    opacity: 0,
                                    transform: "translateY(15px)",
                                  },
                                  to: {
                                    opacity: 1,
                                    transform: "translateY(0)",
                                  },
                                },
                              }}
                            >
                              <CardProduct product={product} />
                            </Grid>
                          ))}
                        </Grid>

                        {/* Pagination */}
                        {totalProducts > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              mt: 5,
                              pt: 3,
                              borderTop: "1px solid #eee",
                            }}
                          >
                            <Pagination
                              count={Math.ceil(totalProducts / itemsPerPage)}
                              page={page}
                              onChange={handlePageChange}
                              shape="rounded"
                              size={isMobile ? "medium" : "large"}
                              sx={{
                                "& .MuiPaginationItem-root": {
                                  fontWeight: 600,
                                  fontSize: "0.9rem",
                                  borderRadius: "8px",
                                  "&.Mui-selected": {
                                    backgroundColor: "#111",
                                    color: "#fff",
                                    "&:hover": {
                                      backgroundColor: "#333",
                                    },
                                  },
                                  "&:hover": {
                                    backgroundColor: "#f5f5f5",
                                  },
                                },
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    </Fade>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: "400px",
                        backgroundColor: "#fafafa",
                        borderRadius: "12px",
                        padding: 4,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "1.25rem",
                          color: "#333",
                          mb: 1,
                        }}
                      >
                        No hay productos disponibles
                      </Typography>
                      <Typography
                        sx={{
                          color: "#666",
                          fontSize: "0.95rem",
                        }}
                      >
                        Intenta ajustando los filtros
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Grid>
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default CategoriesIndex;
